import type { SearchSelectOption, SearchSelectPage } from '@/components/ui';
import type { CouponDefinition } from '@/lib/api-admin';
import { adminFetch } from '@/lib/apiClient';

const BASE = '/api/admin/v1/coupons';
const PAGE_SIZE = 20;

const formatDiscount = (coupon: CouponDefinition): string =>
  coupon.discountType === 'FIXED'
    ? `${coupon.discountAmount.toLocaleString()}원`
    : `${coupon.discountAmount}%`;

// 라벨 정책 (UUID 미노출): title 우선 → badge → code → '제목 없음'.
const toOption = (coupon: CouponDefinition): SearchSelectOption => ({
  value: coupon.id,
  label: coupon.title?.trim() || coupon.badge?.trim() || coupon.code?.trim() || '제목 없음',
  hint: [coupon.couponType === 'PRODUCT' ? '상품 쿠폰' : '장바구니 쿠폰', `할인 ${formatDiscount(coupon)}`]
    .filter(Boolean)
    .join(' · '),
});

interface CouponListEnvelope {
  data?: { content?: CouponDefinition[]; totalElements?: number } | CouponDefinition[];
}

export const fetchCouponOptions = async (
  keyword: string,
  page: number,
): Promise<SearchSelectPage> => {
  const qs = new URLSearchParams({ page: String(page), size: String(PAGE_SIZE) });
  const trimmed = keyword.trim();
  if (trimmed) qs.set('keyword', trimmed);
  const res = await adminFetch(`${BASE}?${qs.toString()}`);
  if (!res.ok) throw new Error(`쿠폰 검색 실패 (${res.status})`);
  const env = (await res.json()) as CouponListEnvelope;
  const raw = env.data;
  const all = Array.isArray(raw) ? raw : (raw?.content ?? []);
  const totalElements = Array.isArray(raw) ? raw.length : (raw?.totalElements ?? all.length);
  const items = all.filter((coupon) => !coupon.deleted).map(toOption);
  const hasMore = Array.isArray(raw) ? false : (page + 1) * PAGE_SIZE < totalElements;
  return { items, hasMore, total: totalElements };
};

export const resolveCouponOption = async (
  couponId: string,
): Promise<SearchSelectOption | null> => {
  const res = await adminFetch(`${BASE}/${couponId}`);
  if (!res.ok) return null;
  const env = (await res.json()) as { data?: CouponDefinition };
  return env.data ? toOption(env.data) : null;
};
